import { useRef, useCallback, useEffect } from "react";
import Reveal from "./Reveal";
import * as Icons from "lucide-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { placeholder, ARIA_PREV, ARIA_NEXT } from "../../lib/constants";

const getSlug = (title) => 
  title.toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');

export default function ServicesHub({ services, sectionContent, uiStrings, globals, elementVisibility, sectionId, contactHref }) {
  const items = services && services.length > 0 ? services : [];
  const loopItems = [...items, ...items, ...items, ...items];
  const trackRef = useRef(null);
  const offsetRef = useRef(0);
  const targetRef = useRef(0);
  const rafRef = useRef(null);
  const intervalRef = useRef(null);
  const isPaused = useRef(false);
  const hasStarted = useRef(false);

  useEffect(() => {
    const el = trackRef.current;
    if (!el || items.length === 0) return;

    const SET_WIDTH = 25;
    const CARD_WIDTH = 25 / items.length;
    const AUTO_SPEED = SET_WIDTH / 1800;
    let lastTime = 0;

    const tick = (time) => {
      if (lastTime === 0) lastTime = time;
      const delta = (time - lastTime) / 16.67;
      lastTime = time;

      if (!isPaused.current) {
        targetRef.current -= AUTO_SPEED * delta;
      }

      offsetRef.current += (targetRef.current - offsetRef.current) * 0.1;

      if (offsetRef.current <= -SET_WIDTH) {
        offsetRef.current += SET_WIDTH;
        targetRef.current += SET_WIDTH;
      }
      if (offsetRef.current > 0) {
        offsetRef.current -= SET_WIDTH;
        targetRef.current -= SET_WIDTH;
      }

      el.style.transform = `translateX(${offsetRef.current}%)`;
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [items.length]);

  const startRepeat = useCallback((dir) => {
    isPaused.current = true;
    const CARD_WIDTH = 25 / items.length;
    targetRef.current -= dir * CARD_WIDTH;
    intervalRef.current = setInterval(() => {
      targetRef.current -= dir * CARD_WIDTH;
    }, 800);
  }, [items.length]);

  const stopRepeat = useCallback(() => {
    clearInterval(intervalRef.current);
    isPaused.current = false;
  }, []);

  const svcSection = uiStrings?.service || {};
  const eyebrow = svcSection.eyebrow || sectionContent?.eyebrow || placeholder("short");
  const headline = svcSection.headline || sectionContent?.headline || placeholder("medium");
  const description = svcSection.description || sectionContent?.description || placeholder("long");
  const learnMoreLabel = svcSection.learn_more || placeholder("short");

  return (
    <section
      data-id="site-services"
      id={sectionId}
      className="relative bg-offwhite py-28 md:py-40"
    >
      <div data-id="services-container" className="max-w-7xl mx-auto px-6 md:px-12">
        <div data-id="services-header" className="grid lg:grid-cols-12 gap-8 md:gap-12 mb-16 md:mb-20">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow text-warmgray" data-id="services-eyebrow">{eyebrow}</p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-softblack mt-4 md:mt-6 leading-[1.05]" data-id="services-headline">
              {headline}
            </h2>
          </Reveal>
          <Reveal delay={200} className="lg:col-span-6 lg:col-start-7 lg:pt-6">
            <p className="text-warmgray text-sm sm:text-base md:text-lg leading-relaxed font-light" data-id="services-description">
              {description}
            </p>
          </Reveal>
        </div>

        {/* Mobile: vertical stack */}
        {elementVisibility?.services_carousel !== false && (
        <div data-id="services-mobile-grid" className="md:hidden grid gap-4">
          {items.map((s, i) => {
            const IconComponent = Icons[s.icon_name] || Icons.HelpCircle;
            const slug = getSlug(s.title);
            return (
              <div
                key={s.uid}
                data-id={`services-mobile-card-${s.uid}`}
                className="group p-6 border border-fendi/70 transition-colors duration-700 hover:bg-fendi/20"
              >
                <a href={`#${slug}`} data-id={`services-mobile-link-${s.uid}`} className="block">
                  <div data-id={`services-mobile-header-${s.uid}`} className="flex items-start justify-between">
                    <span data-id={`services-mobile-num-${s.uid}`} className="eyebrow text-warmgray">{String(i + 1).padStart(2, '0')}</span>
                    <IconComponent size={20} strokeWidth={1} className="text-warmgray" />
                  </div>
                  <h3 className="font-serif text-xl text-softblack mt-4 leading-tight" data-id={`services-mobile-title-${s.uid}`}>
                    {s.title}
                  </h3>
                  <p className="text-warmgray text-sm leading-relaxed mt-3 font-light line-clamp-3 overflow-hidden" data-id={`services-mobile-desc-${s.uid}`}>
                    {s.description}
                  </p>
                  <div data-id={`services-mobile-cta-${s.uid}`} className="mt-5 inline-flex items-center gap-3 text-softblack">
                    <span data-id={`services-mobile-cta-label-${s.uid}`} className="text-[11px] tracking-[0.28em] uppercase font-medium">
                      {learnMoreLabel}
                    </span>
                    <span data-id={`services-mobile-cta-line-${s.uid}`} className="block w-6 h-px bg-softblack group-hover:w-10 group-hover:bg-champagne transition-all duration-500" />
                  </div>
                </a>
              </div>
            );
          })}
        </div>
        )}

        {/* Desktop: infinite smooth horizontal scroll */}
        {elementVisibility?.services_carousel !== false && (
        <div data-id="services-carousel"
          className="hidden md:block overflow-hidden border-t border-fendi/70 relative group/carousel"
          onMouseEnter={() => { isPaused.current = true; }}
          onMouseLeave={() => { isPaused.current = false; }}
        >
          <div data-id="services-carousel-track"
            ref={trackRef}
            className="flex"
            style={{ width: `${items.length * 100}%`, willChange: 'transform' }}
          >
            {loopItems.map((s, i) => {
              const IconComponent = Icons[s.icon_name] || Icons.HelpCircle;
              const slug = getSlug(s.title);
              return (
                <div
                  key={`${s.uid}-${i}`}
                  data-id={`services-carousel-card-${s.uid}`}
                  className="group/card relative p-12 border-b border-fendi/70 border-r border-fendi/70 transition-colors duration-700 hover:bg-fendi/20"
                  style={{ flex: `0 0 ${25 / items.length}%`, height: "520px" }}
                >
                  <a href={`#${slug}`} data-id={`services-carousel-link-${s.uid}`} className="block h-full flex flex-col">
                    <div data-id={`services-carousel-header-${s.uid}`} className="flex items-start justify-between shrink-0">
                      <span data-id={`services-carousel-num-${s.uid}`} className="eyebrow text-warmgray">{String((i % items.length) + 1).padStart(2, '0')}</span>
                      <IconComponent
                        size={22}
                        strokeWidth={1}
                        className="text-warmgray group-hover/card:text-champagne transition-colors duration-500"
                      />
                    </div>
                    <h3 className="font-serif text-[34px] text-softblack mt-12 leading-tight shrink-0" data-id={`services-carousel-title-${s.uid}`}>
                      {s.title}
                    </h3>
                    <p className="text-warmgray text-[15px] leading-relaxed mt-6 font-light line-clamp-3 overflow-hidden" data-id={`services-carousel-desc-${s.uid}`}>
                      {s.description}
                    </p>
                    <div data-id={`services-carousel-cta-${s.uid}`} className="mt-auto inline-flex items-center gap-3 text-softblack">
                      <span data-id={`services-carousel-cta-label-${s.uid}`} className="text-[11px] tracking-[0.28em] uppercase font-medium">
                        {learnMoreLabel}
                      </span>
                      <span data-id={`services-carousel-cta-line-${s.uid}`} className="block w-8 h-px bg-softblack group-hover/card:w-14 group-hover/card:bg-champagne transition-all duration-500" />
                    </div>
                  </a>
                </div>
              );
            })}
          </div>

          {/* Nav buttons */}
          <button data-id="services-carousel-prev"
            type="button"
            onMouseDown={() => startRepeat(-1)}
            onMouseUp={stopRepeat}
            onMouseLeave={stopRepeat}
            aria-label={ARIA_PREV}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-offwhite/90 border border-fendi/40 text-softblack opacity-0 group-hover/carousel:opacity-100 hover:bg-offwhite hover:border-champagne transition-all duration-300 shadow-md"
          >
            <ChevronLeft size={20} />
          </button>
          <button data-id="services-carousel-next"
            type="button"
            onMouseDown={() => startRepeat(1)}
            onMouseUp={stopRepeat}
            onMouseLeave={stopRepeat}
            aria-label={ARIA_NEXT}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-offwhite/90 border border-fendi/40 text-softblack opacity-0 group-hover/carousel:opacity-100 hover:bg-offwhite hover:border-champagne transition-all duration-300 shadow-md"
          >
            <ChevronRight size={20} />
          </button>
        </div>
        )}
      </div>

      {/* Detail sections per service */}
      {elementVisibility?.services_details !== false && items.map((s, i) => (
        <ServiceDetail 
          key={s.uid} 
          service={s} 
          reverse={i % 2 === 1} 
          ctaLabel={svcSection.learn_more || globals?.cta_label || "Saber mais"} 
          contactHref={contactHref} 
        />
      ))}
    </section>
  );
}

function ServiceDetail({ service, reverse, ctaLabel, contactHref }) {
  const slug = getSlug(service.title);
  return (
    <div
      id={slug}
      data-id={`service-detail-${service.uid}`}
      className="max-w-7xl mx-auto px-6 md:px-12 mt-20 md:mt-32 first-of-type:mt-20 md:first-of-type:mt-32 scroll-mt-24"
    >
      <div data-id={`service-detail-grid-${service.uid}`} className={`grid lg:grid-cols-12 gap-12 items-center ${reverse ? "lg:[direction:rtl]" : ""}`}>
        <Reveal className="lg:col-span-7 [direction:ltr]">
          <div data-id={`service-detail-img-wrapper-${service.uid}`} className="relative overflow-hidden">
            <img data-id={`service-detail-img-${service.uid}`}
              src={service.image_url}
              alt={service.title}
              className="w-full h-[240px] sm:h-[320px] md:h-[560px] object-cover transform transition-transform duration-[1800ms] hover:scale-105"
              loading="lazy"
            />
          </div>
        </Reveal>
        <Reveal delay={150} className="lg:col-span-5 [direction:ltr]">
          <h3 className="font-serif text-3xl sm:text-4xl md:text-5xl text-softblack mt-4 md:mt-6 leading-[1.05]" data-id={`service-detail-title-${service.uid}`}>
            {service.title}
          </h3>
          <p className="text-warmgray text-sm md:text-base leading-relaxed mt-4 md:mt-6 font-light" data-id={`service-detail-desc-${service.uid}`}>
            {service.description}
          </p>
          <ul data-id={`service-detail-bullets-${service.uid}`} className="mt-6 md:mt-8 space-y-3 md:space-y-4">
            {service.bullets?.map((b) => (
              <li key={b} data-id={`service-detail-bullet-${service.uid}-${b}`} className="flex items-baseline gap-3 md:gap-4">
                <span data-id={`service-detail-bullet-line-${service.uid}`} className="block w-5 md:w-6 h-px bg-champagne mt-2 shrink-0" />
                <span data-id={`service-detail-bullet-text-${service.uid}`} className="text-softblack/85 text-sm md:text-[15px] font-light">{b}</span>
              </li>
            ))}
          </ul>
          <a href={contactHref || "#contato"} className="btn-ghost mt-8 md:mt-10" data-id={`service-cta-${service.uid}`}>
            {ctaLabel}
          </a>
        </Reveal>
      </div>
    </div>
  );
}
